// @ts-nocheck
/**
 * NewTaskModal — Salesforce-style compact "New Task" creation form.
 *
 * Fields: Subject (required), Due Date, Assigned To, Name (contact), Sales Activity.
 */
import React, { useMemo, useState } from 'react';
import { X, Search, Minimize2, Maximize2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { gobodoUserProvider } from '../../services/providers/gobodoUserProvider';

interface NewTaskModalProps {
  contactName: string;
  assignedTo?: string;
  onClose: () => void;
  onSave: (data: {
    subject: string;
    dueDate: string;
    assignedTo: string;
    contactName: string;
    salesActivity: string;
  }) => void;
}

const NewTaskModal: React.FC<NewTaskModalProps> = ({
  contactName,
  assignedTo = '',
  onClose,
  onSave,
}) => {
  const [subject, setSubject] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignee, setAssignee] = useState(assignedTo);
  const [contact, setContact] = useState(contactName);
  const [salesActivity, setSalesActivity] = useState('');
  const [subjectError, setSubjectError] = useState(false);
  const { data: users = [] } = useQuery({
    queryKey: ['credit-block', 'task-modal', 'users'],
    queryFn: async () => gobodoUserProvider.getUsers(),
    enabled: shouldUseGobodo(),
    staleTime: 5 * 60 * 1000,
  });
  const assigneeOptions = useMemo(() => {
    const opts = (users ?? [])
      .map((u) => {
        const fullName = `${String(u.firstName ?? '').trim()} ${String(u.lastName ?? '').trim()}`.trim();
        return fullName || String(u.username ?? '').trim() || String(u.email ?? '').trim();
      })
      .filter(Boolean);
    const deduped = Array.from(new Set(opts));
    if (assignedTo && !deduped.includes(assignedTo)) deduped.unshift(assignedTo);
    return deduped;
  }, [users, assignedTo]);

  const handleSave = () => {
    if (!subject.trim()) {
      setSubjectError(true);
      return;
    }
    onSave({
      subject: subject.trim(),
      dueDate,
      assignedTo: assignee,
      contactName: contact,
      salesActivity,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-600 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 rounded-t-lg">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-5 h-5 rounded bg-green-600 text-white text-[10px] font-bold">T</span>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">New Task</h3>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="Minimize">
              <Minimize2 className="w-3.5 h-3.5" />
            </button>
            <button type="button" className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="Expand">
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="Close">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              <span className="text-red-500">*</span> Subject
            </label>
            <div className="relative">
              <input
                type="text"
                value={subject}
                onChange={(e) => { setSubject(e.target.value); setSubjectError(false); }}
                className={`w-full px-3 py-2 pr-8 text-sm border rounded ${
                  subjectError
                    ? 'border-red-500 ring-1 ring-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100`}
                autoFocus
              />
              <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            {subjectError && (
              <p className="text-xs text-red-600 mt-1">Complete this field.</p>
            )}
          </div>

          {/* Due Date + Assigned To */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                <span className="text-red-500">*</span> Due Date
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                <span className="text-red-500">*</span> Assigned To
              </label>
              {assigneeOptions.length > 0 ? (
                <select
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <option value="">--None--</option>
                  {assigneeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : assignee ? (
                <div className="flex items-center gap-1.5 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold">
                    {assignee.charAt(0).toUpperCase()}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100 flex-1 truncate">{assignee}</span>
                  <button type="button" onClick={() => setAssignee('')} className="text-gray-400 hover:text-gray-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              )}
            </div>
          </div>

          {/* Name + Sales Activity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              {contact ? (
                <div className="flex items-center gap-1.5 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900">
                  <span className="flex items-center justify-center w-5 h-5 rounded bg-purple-600 text-white text-[10px] font-bold">
                    {contact.charAt(0).toUpperCase()}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100 flex-1 truncate">{contact}</span>
                  <button type="button" onClick={() => setContact('')} className="text-gray-400 hover:text-gray-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                <span className="text-red-500">*</span> Sales Activity
              </label>
              <select
                value={salesActivity}
                onChange={(e) => setSalesActivity(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              >
                <option value="">--None--</option>
                <option value="call">Call</option>
                <option value="email">Email</option>
                <option value="meeting">Meeting</option>
                <option value="follow_up">Follow Up</option>
                <option value="send_letter">Send Letter</option>
                <option value="cancellation">Cancellation</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewTaskModal;
